import { useEffect, useMemo, useRef, useState } from "react";

import { CloseMD, SearchMagnifyingGlass } from "@/icons";
import { useResponsiveness, useTranslate } from "@/providers/context";
import { cn } from "@/utils";

import { Button, Input } from "..";

interface SearchBarProps {
  className?: string;
  value?: string | null;
  onSearch: (value: string) => void;
  collapsible?: boolean;
  showClearButton?: boolean;
}

export const SearchBar = ({
  className,
  value,
  onSearch,
  collapsible,
  showClearButton,
}: SearchBarProps) => {
  const [val, setVal] = useState<string>(value ?? "");
  const [isExpanded, setIsExpanded] = useState<boolean>(!!val || !collapsible);
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const { colsPerRow } = useResponsiveness();
  const { translate } = useTranslate();

  useEffect(() => {
    setVal(value ?? "");
  }, [value]);

  useEffect(() => {
    if (collapsible && !val && !isFocused && isExpanded) {
      setIsExpanded(false);
    }
  }, [val, isFocused, isExpanded, collapsible]);

  const handleIconClick = () => {
    if (collapsible && !isExpanded) {
      handleInputFocus();
    }
  };

  const handleInputFocus = () => {
    setIsFocused(true);
    if (collapsible && !isExpanded) {
      setIsExpanded(true);
      inputRef.current?.focus();
    }
  };

  const handleInputBlur = () => {
    setIsFocused(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setVal(e.target.value);
  };

  const inputWidth = useMemo(() => {
    if (collapsible && !isExpanded) {
      return "24px";
    }
    return colsPerRow < 3 ? "230px" : "393px";
  }, [collapsible, isExpanded, colsPerRow]);

  const isCollapsed = useMemo(() => {
    return !!collapsible && !isExpanded;
  }, [collapsible, isExpanded]);

  return (
    <form
      className={cn("pr-[8px]", className)}
      onSubmit={(e) => {
        e.preventDefault();
        onSearch(val);
      }}
    >
      <div className="group relative">
        <SearchMagnifyingGlass
          className={cn(
            "absolute left-3 top-[50%] translate-y-[-50%] text-item-secondary",
            collapsible ? "h-6 w-6" : "h-5 w-5",
            (isFocused || val) && "text-item-primary",
            isCollapsed &&
              "left-1/2 -translate-x-1/2 cursor-pointer text-item-primary hover:text-item-secondary",
          )}
          onClick={handleIconClick}
        />
        <Input
          ref={inputRef}
          className={cn(
            "p2-medium h-10 py-[10px] pr-3",
            showClearButton && "pr-[30px]",
            collapsible ? "pl-10" : "pl-9",
            isCollapsed ? "pointer-events-none px-0 opacity-0" : "opacity-100",
            "group-hover:bg-surface-tertiary",
          )}
          name="search"
          style={{ width: inputWidth, transition: "width 0.15s ease-in-out" }}
          type="text"
          placeholder={isCollapsed ? "" : translate("Search")}
          value={val}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
        />
        {showClearButton && !isCollapsed && !!val && (
          <Button
            variant="tertiary"
            size="s"
            icon={<CloseMD height={16} width={16} />}
            className="absolute right-[8px] top-1/2 h-[20px] w-[20px] -translate-y-1/2 p-0 hover:bg-transparent hover:text-item-secondary"
            onClick={() => {
              setVal("");
              onSearch("");
            }}
          />
        )}
      </div>
    </form>
  );
};
